<template>
    <button class="code-bot-info" type="button" data-bs-toggle="modal" :data-bs-target="`#${modalId}`">
        <img src="@/assets/svg_icons/lightbulb.svg" alt="" aria-hidden="true">
        <slot></slot>
    </button>

    <Teleport to="body">
        <div class="modal fade bot-guide-modal" :id="modalId" tabindex="-1" :aria-labelledby="`${modalId}-title`"
            aria-hidden="true">
            <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <div>
                            <p class="modal-kicker">Bot Guide</p>
                            <h1 class="modal-title fs-5" :id="`${modalId}-title`">Bot 代码入门教程</h1>
                        </div>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <section class="guide-section">
                            <h2>关于 Bot</h2>
                            <p>{{ botinfo.title }}</p>
                        </section>

                        <section class="guide-section">
                            <h2>需要实现的接口</h2>
                            <VAceEditor readonly v-model:value="botinfo.description" lang="c_cpp" theme="textmate"
                                class="guide-editor guide-editor-sm" :options="plainEditorOptions" />
                        </section>

                        <section class="guide-section">
                            <div class="guide-section-header">
                                <h2>示例代码</h2>
                                <button class="btn btn-primary btn-sm" type="button" @click="copyCode">
                                    一键复制
                                </button>
                            </div>

                            <div v-if="copy_success" class="copy-toast success" role="status">
                                复制成功，快去创建你的 Bot 吧！
                            </div>
                            <div v-if="copy_fail" class="copy-toast danger" role="status">
                                复制失败，请重试或检查浏览器权限。
                            </div>

                            <VAceEditor readonly v-model:value="botinfo.content" lang="c_cpp" theme="textmate"
                                class="guide-editor guide-editor-lg" :options="codeEditorOptions" />
                        </section>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-primary" data-bs-dismiss="modal">我已知晓</button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script>
import { getCurrentInstance, reactive, ref } from 'vue';
import { VAceEditor } from 'vue3-ace-editor';
import ace from 'ace-builds';
import 'ace-builds/src-noconflict/mode-c_cpp';
import 'ace-builds/src-noconflict/theme-textmate';
import 'ace-builds/src-noconflict/ext-language_tools';
import { codeExample } from '@/assets/scripts/ExampleCode';

export default {
    components: {
        VAceEditor
    },

    setup() {
        ace.config.set(
            "basePath",
            "https://cdn.jsdelivr.net/npm/ace-builds@" +
            require('ace-builds').version +
            "/src-noconflict/"
        );

        const instance = getCurrentInstance();
        const modalId = `code-bot-info-${instance.uid}`;

        const botinfo = reactive({
            title: "你需要实现 BotInterface 接口的 nextMove 方法。蛇的每一次移动都会调用 nextMove 方法，用它来获取下一步方向。nextMove 的返回值为 0、1、2、3，分别代表上、右、下、左。",
            description: `package com.kob.botrunningsystem.utils;

public interface BotInterface {
    Integer nextMove(String input);
}`,
            content: codeExample,
        });

        const plainEditorOptions = {
            fontSize: 17,
            tabSize: 4,
            showPrintMargin: false,
            highlightActiveLine: false,
            showLineNumbers: false,
            showFoldWidgets: false,
            readOnly: true,
        };

        const codeEditorOptions = {
            enableBasicAutocompletion: true,
            enableSnippets: true,
            enableLiveAutocompletion: true,
            fontSize: 17,
            tabSize: 4,
            showPrintMargin: false,
            highlightActiveLine: true,
            readOnly: true,
        };

        const copy_success = ref(false);
        const copy_fail = ref(false);

        const showCopyState = (target) => {
            copy_success.value = target === "success";
            copy_fail.value = target === "fail";
            setTimeout(() => {
                copy_success.value = false;
                copy_fail.value = false;
            }, 1400);
        };

        const copyCode = () => {
            if (!navigator.clipboard) {
                showCopyState("fail");
                return;
            }
            navigator.clipboard.writeText(botinfo.content)
                .then(() => showCopyState("success"))
                .catch(() => showCopyState("fail"));
        };

        return {
            modalId,
            botinfo,
            plainEditorOptions,
            codeEditorOptions,
            copy_success,
            copy_fail,
            copyCode,
        };
    }
};
</script>

<style scoped>
.code-bot-info {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    padding: 0;
    color: var(--app-primary);
    font: inherit;
    font-weight: 700;
    background: transparent;
    border: 0;
}

.code-bot-info img {
    width: 18px;
    height: 18px;
}

.code-bot-info:hover {
    color: var(--app-primary-strong);
}

.bot-guide-modal .modal-header {
    align-items: flex-start;
    padding: 20px 24px;
}

.modal-kicker {
    margin: 0 0 4px;
    color: var(--app-muted);
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0;
    text-transform: uppercase;
}

.bot-guide-modal .modal-body {
    padding: 22px 24px;
}

.guide-section + .guide-section {
    margin-top: 22px;
}

.guide-section h2 {
    margin: 0 0 10px;
    color: var(--app-text);
    font-size: 1rem;
    font-weight: 800;
}

.guide-section p {
    margin: 0;
    padding: 14px 16px;
    color: var(--app-text);
    line-height: 1.8;
    background: var(--app-surface-muted);
    border: 1px solid var(--app-border);
    border-radius: 10px;
}

.guide-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
}

.guide-section-header h2 {
    margin: 0;
}

.guide-editor {
    border: 1px solid var(--app-border);
    border-radius: 10px;
    overflow: hidden;
}

.guide-editor-sm {
    height: 116px;
}

.guide-editor-lg {
    height: 330px;
}

.copy-toast {
    margin-bottom: 10px;
    padding: 10px 14px;
    color: var(--app-text);
    font-weight: 700;
    border-radius: 10px;
}

.copy-toast.success {
    background: rgba(34, 197, 94, 0.14);
    border: 1px solid rgba(34, 197, 94, 0.38);
}

.copy-toast.danger {
    background: rgba(239, 68, 68, 0.14);
    border: 1px solid rgba(239, 68, 68, 0.38);
}
</style>
